import { createContext, SetStateAction, useContext, useState } from "react";

export interface AccountGroups {
  accountGroupId: string;
  accountGroupsName: string;
  sortOrder: number;
  isSelected: boolean;
  shared: number;  // 0:非共有, 1:共有
  isOwner: number; // 0:共有者でない, 1:共有者
  accountsList: AccountModel[];
  getFinance: boolean; // 口座グループに銀行API口座が含まれるか
  getCreca: boolean; // 口座グループにクレカAPI口座が含まれるか
  getPaymentCreca: boolean; // 口座グループにLookクレカ口座が含まれるか
  getPaymentElectronicmoney: boolean; // 口座グループにLook電子マネー口座が含まれるか
  getEcshop: boolean; // 口座グループにECショップAPI口座が含まれるか
  getCsvexcel: boolean; // 口座グループにCSV/Excel口座が含まれるか
  getOcr: boolean; // 口座グループにOCR口座が含まれるか
  getScrapFinance: boolean; // 口座グループにスクレイピング銀行口座が含まれるか
}

export interface AccountModel {
  mlinkAccountId:string; // finance_account_idやcreca_account_id等の口座アカウントID
  mlinkId: string; // finance_idやcreca_id等の口座ID
  passbookName: string;
  passbookType: string;
  lastAggregate: string;
}

export interface Accounts {
  industryCode: string;
  companyCode: string;
  companyName: string;
  serviceCode: string;
  serviceName: string;
  branchCode: string;
  branchName: string;
  passbookName: string;
  balance: string;
  authorizationStatus: number; // 0:未認可、1:認可、2:再認可、3:未契約、4:無効、9: ML
  selectedStatus: number; // 口座選択状態(0:未選択、1:選択中、2:削除)
  accountGroupId: string;
  mlinkAccountId: string;
  mlinkId: string;
  passbookType: string;
  registeredDate: string;
  scrapeType: number; // 0:対象外、1:インストール版(使用しない)、2:サーバ版スクレイピング、3:API版
  ocrProcessId: string;  // OCR口座のみに持たせる明細登録管理のID
  ocrProcessStatus: number; // OCR口座のみに持たせる明細登録の処理状況
  otpYn: string; // ワンパス発生有無 0：OTP画面は表示されない, 1：必ずOTP画面が表示される, 2：ユーザー設定次第でOTP画面が表示される
  isUnderMaintenance: boolean; // メンテナンスフラグ
}

interface InviteInfo {
  sharedId: string;
  ownerId: string;
  accountGroupId: string;
  accountGroupName: string;
}

interface AccountGroupsContextType {
  accountGroups: AccountGroups[];
  accounts: Accounts[];
  selectedAccountGroup: AccountGroups; // ホーム画面の選択中の口座グループ
  userSelectedAccountGroupId: string;
  selectedAccount: Accounts | undefined;
  targetAccountGroup: AccountGroups; // MYデータ画面での処理対象の口座グループ
  invitedAccountGroups: InviteInfo[]; // 招待された口座グループ
  setAccountGroups: React.Dispatch<SetStateAction<AccountGroups[]>>;
  setAccounts: React.Dispatch<SetStateAction<Accounts[]>>;
  setSelectedAccountGroup: React.Dispatch<SetStateAction<AccountGroups>>;
  setUserSelectedAccountGroupId: React.Dispatch<SetStateAction<string>>;
  setSelectedAccount: React.Dispatch<SetStateAction<Accounts | undefined>>;
  resetSelectedAccountGroup: () => void;
  setTargetAccountGroup: React.Dispatch<SetStateAction<AccountGroups>>;
  resetTargetAccountGroup: () => void;
  setInvitedAccountGroups: React.Dispatch<SetStateAction<InviteInfo[]>>;
}

const initialSelectedAccountGroup: AccountGroups = {
  accountGroupId: "",
  accountGroupsName: "",
  sortOrder: 0,
  isSelected: false,
  shared: 0,
  isOwner: 0,
  accountsList: [],
  getFinance: false,
  getCreca: false,
  getPaymentCreca: false,
  getPaymentElectronicmoney: false,
  getEcshop: false,
  getCsvexcel: false,
  getOcr: false,
  getScrapFinance: false,
};

const initialTargetAccountGroup: AccountGroups = {
  accountGroupId: "",
  accountGroupsName: "",
  sortOrder: 0,
  isSelected: false,
  shared: 0,
  isOwner: 0,
  accountsList: [],
  getFinance: false,
  getCreca: false,
  getPaymentCreca: false,
  getPaymentElectronicmoney: false,
  getEcshop: false,
  getCsvexcel: false,
  getOcr: false,
  getScrapFinance: false,
};

const AccountGroupsContext = createContext<AccountGroupsContextType | undefined>(undefined);

export const AccountGroupsProvider = ({children}: {children: React.ReactNode}) => {
  const [accountGroups, setAccountGroups] = useState<AccountGroups[]>([]);
  const [accounts, setAccounts] = useState<Accounts[]>([]);
  const [selectedAccountGroup, setSelectedAccountGroup] = useState<AccountGroups>(initialSelectedAccountGroup);
  const [userSelectedAccountGroupId, setUserSelectedAccountGroupId] = useState<string>("");
  const [selectedAccount, setSelectedAccount] = useState<Accounts | undefined>(undefined);
  const [targetAccountGroup, setTargetAccountGroup] = useState<AccountGroups>(initialTargetAccountGroup);
  const [invitedAccountGroups, setInvitedAccountGroups] = useState<InviteInfo[]>([]);

  const resetSelectedAccountGroup = () => {
    setSelectedAccountGroup(initialSelectedAccountGroup);
  };
  const resetTargetAccountGroup = () => {
    setTargetAccountGroup(initialTargetAccountGroup);
  };

  return (
    <AccountGroupsContext.Provider
      value={{
        accountGroups,
        accounts,
        selectedAccountGroup,
        userSelectedAccountGroupId,
        selectedAccount,
        targetAccountGroup,
        invitedAccountGroups,
        setAccountGroups,
        setAccounts,
        setSelectedAccountGroup,
        setUserSelectedAccountGroupId,
        setSelectedAccount,
        resetSelectedAccountGroup,
        setTargetAccountGroup,
        resetTargetAccountGroup,
        setInvitedAccountGroups,
      }}
    >
      {children}
    </AccountGroupsContext.Provider>
  );
};

export const useAccountGroupsContext = () => {
  const context = useContext(AccountGroupsContext);
  if (!context) throw new Error("AccountGroupsContext must be used within AccountGroupsProvider");
  return context;
};